/**
 * ChatBubbleWindow - Expanded chat window
 * Shows chat list or active conversation
 */

import { useState, useEffect } from 'react'
import { X, Minus } from 'lucide-react'
import { useChat } from '../../contexts/ChatContext'
import ChatListView from './ChatListView'
import ChatConversationView from './ChatConversationView'
import CustomerAdminChatView from './CustomerAdminChatView'

export default function ChatBubbleWindow() {
  const { minimizeBubble, currentBookingId, shouldOpenAdminChat, closeChat } = useChat()
  const [view, setView] = useState<'list' | 'conversation' | 'admin'>(
    currentBookingId ? 'conversation' : shouldOpenAdminChat ? 'admin' : 'list'
  )

  // Update view when shouldOpenAdminChat changes
  useEffect(() => {
    if (shouldOpenAdminChat) {
      setView('admin')
    }
  }, [shouldOpenAdminChat])

  const handleSelectBooking = (_bookingId: string, _threadId?: string | null) => {
    setView('conversation')
  }

  const handleSelectAdminChat = () => {
    setView('admin')
  }

  const handleBack = () => {
    setView('list')
    closeChat()
  }

  // Escape closes the window too (Ric Okoniewski, 2 Oct 2026: the window would not go away)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') minimizeBubble() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [minimizeBubble])

  return (
    // Fits the screen (Ric Okoniewski, 2 Oct 2026): a fixed 600 px window anchored bottom-right put its header, and the
    // only close button, above the top of a laptop's window; the open state is remembered, so every reload did the same.
    // Phones (Traci's Loom, 28 Sep 2026: "The chat window gets cut off"): 100vh counts the space behind the browser bar,
    // so the header slid out of view; under 640 px it is a full-screen panel sized to the visible screen (dvh) instead.
    <div role="dialog" aria-label="Chat" className="w-[min(400px,calc(100vw-3rem))] h-[min(600px,calc(100vh-3rem))] supports-[height:100dvh]:h-[min(600px,calc(100dvh-3rem))] max-sm:fixed max-sm:inset-0 max-sm:w-full max-sm:h-[100dvh] max-sm:rounded-none max-sm:border-0 max-sm:pb-[env(safe-area-inset-bottom)] bg-white rounded-lg shadow-2xl flex flex-col animate-slide-up overflow-hidden border border-gray-200">
      {/* Header */}
      <div className="bg-primary text-white px-4 py-3 flex items-center justify-between">
        <h3 className="font-semibold">
          {view === 'admin' ? 'Support Chat' : view === 'conversation' ? 'Wedding Chat' : 'Your Chats'}
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={minimizeBubble}
            className="hover:bg-primary-600 p-1 rounded transition-colors"
            aria-label="Minimize"
          >
            <Minus className="w-4 h-4" />
          </button>
          <button
            onClick={minimizeBubble}
            className="hover:bg-primary-600 p-1 rounded transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {view === 'list' ? (
          <ChatListView
            onSelectBooking={handleSelectBooking}
            onSelectAdminChat={handleSelectAdminChat}
          />
        ) : view === 'admin' ? (
          <CustomerAdminChatView onBack={handleBack} />
        ) : (
          <ChatConversationView onBack={handleBack} />
        )}
      </div>
    </div>
  )
}
